/**
 * คำขอ "พนักงานเริ่มงานใหม่" — HR ยื่นคำขอ แล้ว HR/เจ้าของกดอนุมัติ ระบบถึงจะสร้าง
 * พนักงานจริง (เหมือนใบลา/OT ที่ต้องมีคนอนุมัติก่อนมีผล).
 *
 * HR + เจ้าของ → เห็นฟอร์มยื่น + ปุ่มอนุมัติ/ไม่อนุมัติบนคำขอที่ยังรออยู่
 *   (fb#79: HR อนุมัติเองได้ — backend เช็คสิทธิ์ edit_employee, HR ระดับที่ไม่มีสิทธิ์ได้ข้อความแจ้ง)
 *
 * อนุมัติแล้วระบบจะสร้างพนักงานสถานะ "รอเริ่มงาน" พร้อมรหัสพนักงาน — ขั้นต่อไป
 * คือส่งลิงก์/QR ให้พนักงานผูกไลน์ที่หน้า "นำเข้าพนักงาน"
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { UserPlus, Check, X, Clock } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { Loading } from '../../components/layout/Loading';
import { useToast } from '../../components/ui/toast';
import { useAuthStore } from '../../lib/store';
import {
  useOnboardingRequests,
  useSubmitOnboardingRequest,
  useDecidePendingChange,
  type OnboardingRequest,
} from '../../hooks';

type FormData = {
  first_name: string;
  last_name: string;
  national_id: string;
  department: string;
  position: string;
  start_date: string;
  note: string;
};

const STATUS_BADGE: Record<OnboardingRequest['status'], { label: string; cls: string }> = {
  pending: { label: 'รออนุมัติ', cls: 'badge-warn' },
  approved: { label: 'อนุมัติแล้ว', cls: 'badge-success' },
  rejected: { label: 'ไม่อนุมัติ', cls: 'badge-danger' },
};

function thaiDate(iso: string): string {
  if (!iso) return '-';
  return new Date(iso.length > 10 ? iso : `${iso}T00:00:00+07:00`).toLocaleDateString('th-TH', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function HRNewHireRequestsContent() {
  const role = useAuthStore((s) => s.profile?.role ?? 'EMPLOYEE');
  const isOwner = role === 'OWNER';
  const canDecide = isOwner || role === 'HR';
  const { data, isLoading } = useOnboardingRequests();
  const submit = useSubmitOnboardingRequest();
  const decide = useDecidePendingChange();
  const toast = useToast();
  const [formOpen, setFormOpen] = useState(false);
  const [rejectFor, setRejectFor] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>();

  const requests = data ?? [];
  const pending = requests.filter((r) => r.status === 'pending');
  const decided = requests.filter((r) => r.status !== 'pending');

  async function onSubmit(form: FormData) {
    try {
      await submit.mutateAsync({
        first_name: form.first_name,
        last_name: form.last_name,
        national_id: form.national_id?.trim() || undefined,
        department: form.department?.trim() || undefined,
        position: form.position?.trim() || undefined,
        start_date: form.start_date || undefined,
        note: form.note?.trim() || undefined,
      });
      toast.success('ส่งคำขอแล้ว — กดอนุมัติในรายการด้านล่างเพื่อสร้างพนักงาน');
      reset();
      setFormOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ส่งคำขอไม่สำเร็จ');
    }
  }

  async function approve(r: OnboardingRequest) {
    try {
      const res = await decide.mutateAsync({ change_id: r.change_id, decision: 'approved', onboarding: !isOwner });
      toast.success(res?.emp_code ? `อนุมัติแล้ว — สร้างพนักงาน ${res.emp_code}` : 'อนุมัติแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'อนุมัติไม่สำเร็จ');
    }
  }

  async function submitReject() {
    if (!rejectFor || !rejectNote.trim()) {
      toast.error('ระบุเหตุผลที่ไม่อนุมัติ');
      return;
    }
    try {
      await decide.mutateAsync({ change_id: rejectFor, decision: 'rejected', decision_note: rejectNote.trim(), onboarding: !isOwner });
      setRejectFor(null);
      setRejectNote('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <>
      <div className="flex items-start justify-between gap-3 mb-3">
        <p className="text-sm text-gray-600">
          ยื่นคำขอรับพนักงานใหม่ แล้วฝ่ายบุคคลหรือเจ้าของกดอนุมัติ — อนุมัติแล้วระบบจะสร้างพนักงาน
          สถานะ "รอเริ่มงาน" พร้อมรหัสพนักงานให้อัตโนมัติ
        </p>
        <button onClick={() => setFormOpen(true)} className="btn-cta btn-slim whitespace-nowrap">
          <UserPlus className="w-4 h-4" /> ยื่นคำขอ
        </button>
      </div>

      {isLoading ? (
        <Loading />
      ) : requests.length === 0 ? (
        <div className="card p-6 text-center text-sm text-gray-500">ยังไม่มีคำขอรับพนักงานใหม่</div>
      ) : (
        <div className="space-y-4">
          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              รออนุมัติ ({pending.length})
            </h3>
            {pending.length === 0 ? (
              <div className="card p-4 text-sm text-gray-500">ไม่มีคำขอค้าง</div>
            ) : (
              <div className="space-y-2">
                {pending.map((r) => (
                  <div key={r.change_id} className="card p-4">
                    <div className="flex justify-between items-start gap-3">
                      <div className="min-w-0">
                        <p className="font-medium">{r.name}</p>
                        <p className="text-xs text-gray-500">
                          {[r.position, r.department].filter(Boolean).join(' · ') || 'ยังไม่ระบุตำแหน่ง/แผนก'}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          เริ่มงาน: {r.start_date ? thaiDate(r.start_date) : 'ยังไม่ระบุ'}
                          {r.has_nid ? ' · มีเลขบัตร' : ' · ยังไม่มีเลขบัตร (ผูกไลน์ด้วยรหัสจับคู่)'}
                        </p>
                        {r.note && <p className="text-xs text-gray-600 mt-1">หมายเหตุ: {r.note}</p>}
                      </div>
                      <span className={STATUS_BADGE[r.status].cls}>{STATUS_BADGE[r.status].label}</span>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-2">
                      <Clock className="w-3 h-3 inline mr-1" />
                      ยื่นเมื่อ {new Date(r.created_at).toLocaleString('th-TH')} · {r.change_id}
                    </p>
                    {canDecide && (
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => approve(r)} disabled={decide.isPending} className="btn-cta flex-1">
                          <Check className="w-4 h-4" /> อนุมัติ
                        </button>
                        <button
                          onClick={() => { setRejectFor(r.change_id); setRejectNote(''); }}
                          disabled={decide.isPending}
                          className="btn-danger"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {decided.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">ตัดสินแล้ว</h3>
              <div className="rounded-lg border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs">
                    <tr>
                      <th className="px-3 py-2 text-left">ชื่อ</th>
                      <th className="px-3 py-2 text-left">ตำแหน่ง / แผนก</th>
                      <th className="px-3 py-2 text-left">เริ่มงาน</th>
                      <th className="px-3 py-2 text-left">ผล</th>
                    </tr>
                  </thead>
                  <tbody>
                    {decided.map((r) => (
                      <tr key={r.change_id} className="border-t border-gray-100">
                        <td className="px-3 py-2">{r.name}</td>
                        <td className="px-3 py-2 text-gray-600">
                          {[r.position, r.department].filter(Boolean).join(' · ') || '-'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{r.start_date ? thaiDate(r.start_date) : '-'}</td>
                        <td className="px-3 py-2">
                          <span className={STATUS_BADGE[r.status].cls}>{STATUS_BADGE[r.status].label}</span>
                          {r.decision_note && (
                            <span className="block text-xs text-gray-500 mt-0.5">{r.decision_note}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}

      <Modal open={formOpen} onClose={() => setFormOpen(false)} title="คำขอพนักงานเริ่มงานใหม่">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">ชื่อ *</label>
              <input {...register('first_name', { required: true })} className="input" />
              {errors.first_name && <p className="text-xs text-red-600 mt-1">กรอกชื่อ</p>}
            </div>
            <div>
              <label className="label">นามสกุล *</label>
              <input {...register('last_name', { required: true })} className="input" />
              {errors.last_name && <p className="text-xs text-red-600 mt-1">กรอกนามสกุล</p>}
            </div>
          </div>
          <div>
            <label className="label">เลขบัตรประชาชน 13 หลัก</label>
            <input {...register('national_id')} className="input" inputMode="numeric" placeholder="ไม่ใส่ก็ได้ — ระบบจะออกรหัสจับคู่ให้แทน" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">แผนก</label>
              <input {...register('department')} className="input" />
            </div>
            <div>
              <label className="label">ตำแหน่ง</label>
              <input {...register('position')} className="input" />
            </div>
          </div>
          <div>
            <label className="label">วันเริ่มงาน</label>
            <input type="date" {...register('start_date')} className="input" />
          </div>
          <div>
            <label className="label">หมายเหตุถึงผู้อนุมัติ</label>
            <textarea {...register('note')} rows={2} className="input" placeholder="เช่น แทนตำแหน่งที่ลาออก / ตกลงค่าจ้างไว้เท่าไหร่" />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={() => setFormOpen(false)} className="btn-secondary flex-1">ยกเลิก</button>
            <button type="submit" disabled={submit.isPending} className="btn-primary flex-1">
              {submit.isPending ? 'กำลังส่ง…' : 'ส่งคำขอ'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={rejectFor !== null} onClose={() => setRejectFor(null)} title="ไม่อนุมัติคำขอ">
        <div className="space-y-3">
          <div>
            <label className="label">เหตุผล</label>
            <textarea className="input" rows={3} value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="ระบุเหตุผลให้ HR รู้…" />
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setRejectFor(null)}>ยกเลิก</button>
            <button className="btn-danger flex-1" onClick={submitReject} disabled={decide.isPending}>ไม่อนุมัติ</button>
          </div>
        </div>
      </Modal>
    </>
  );
}
